import { useState, useCallback, useEffect } from 'react';

export interface SavedFilterView {
  id: string;
  name: string;
  filters: Record<string, any>;
  createdAt: string;
  icon?: string;
  isDefault?: boolean;
}

interface UseSavedFilterViewsOptions {
  storageKey: string;
  defaultViews?: SavedFilterView[];
}

interface UseSavedFilterViewsReturn {
  views: SavedFilterView[];
  activeViewId: string | null;
  activeView: SavedFilterView | null;
  saveView: (name: string, filters: Record<string, any>, icon?: string) => string;
  loadView: (viewId: string) => Record<string, any> | null;
  deleteView: (viewId: string) => void;
  renameView: (viewId: string, newName: string) => void;
  setActiveView: (viewId: string | null) => void;
  updateViewFilters: (viewId: string, filters: Record<string, any>) => void;
}

export function useSavedFilterViews({
  storageKey,
  defaultViews = [],
}: UseSavedFilterViewsOptions): UseSavedFilterViewsReturn {
  const generateId = () => `view_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

  const loadFromStorage = (): SavedFilterView[] => {
    try {
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        const parsed = JSON.parse(stored) as SavedFilterView[];
        // Merge with default views (default views cannot be deleted)
        const storedIds = new Set(parsed.map(v => v.id));
        const merged = [...parsed];
        for (const dv of defaultViews) {
          if (!storedIds.has(dv.id)) {
            merged.unshift({ ...dv, isDefault: true });
          }
        }
        return merged;
      }
    } catch (e) {
      console.warn('Failed to load saved views from storage:', e);
    }
    return defaultViews.map(v => ({ ...v, isDefault: true }));
  };

  const loadActiveViewFromStorage = (): string | null => {
    try {
      const stored = localStorage.getItem(`${storageKey}_active`);
      return stored || null;
    } catch (e) {
      return null;
    }
  };

  const [views, setViews] = useState<SavedFilterView[]>(loadFromStorage);
  const [activeViewId, setActiveViewIdState] = useState<string | null>(loadActiveViewFromStorage);

  // Save to storage when views change
  useEffect(() => {
    try {
      // Only save non-default views to storage
      const toSave = views.filter(v => !v.isDefault);
      localStorage.setItem(storageKey, JSON.stringify(toSave));
    } catch (e) {
      console.warn('Failed to save views to storage:', e);
    }
  }, [views, storageKey]);

  // Save active view to storage
  useEffect(() => {
    try {
      if (activeViewId) {
        localStorage.setItem(`${storageKey}_active`, activeViewId);
      } else {
        localStorage.removeItem(`${storageKey}_active`);
      }
    } catch (e) {
      console.warn('Failed to save active view to storage:', e);
    }
  }, [activeViewId, storageKey]);

  const saveView = useCallback((name: string, filters: Record<string, any>, icon?: string): string => {
    const id = generateId();
    const newView: SavedFilterView = {
      id,
      name,
      filters,
      createdAt: new Date().toISOString(),
      icon,
    };
    setViews(prev => [...prev, newView]);
    return id;
  }, []);

  const loadView = useCallback((viewId: string): Record<string, any> | null => {
    const view = views.find(v => v.id === viewId);
    return view?.filters ?? null;
  }, [views]);

  const deleteView = useCallback((viewId: string) => {
    setViews(prev => prev.filter(v => v.id !== viewId || v.isDefault));
    if (activeViewId === viewId) {
      setActiveViewIdState(null);
    }
  }, [activeViewId]);

  const renameView = useCallback((viewId: string, newName: string) => {
    setViews(prev =>
      prev.map(v =>
        v.id === viewId && !v.isDefault
          ? { ...v, name: newName }
          : v
      )
    );
  }, []);

  const setActiveView = useCallback((viewId: string | null) => {
    setActiveViewIdState(viewId);
  }, []);

  const updateViewFilters = useCallback((viewId: string, filters: Record<string, any>) => {
    setViews(prev =>
      prev.map(v =>
        v.id === viewId && !v.isDefault
          ? { ...v, filters }
          : v
      )
    );
  }, []);

  const activeView = views.find(v => v.id === activeViewId) ?? null;

  return {
    views,
    activeViewId,
    activeView,
    saveView,
    loadView,
    deleteView,
    renameView,
    setActiveView,
    updateViewFilters,
  };
}

export default useSavedFilterViews;
